import { forwardRef, useImperativeHandle } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { WebView } from '@zenbusiness/application-commons-webview';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { OWNER_CONTRIBUTIONS_URL } from '#client/constants/FAQs';
import { MODAL_NAMES } from '#client/constants/modals';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import usePlaid from '#client/hooks/usePlaid';
import useRedirectToLinkedAccounts, {
  LINK_INTENT
} from '#client/hooks/useRedirectToLinkedAccounts';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import getLastSegmentUrl from '#client/utils/getLastSegmentUrl';
import { linkPlaidWithNativeApp } from '#client/utils/Plaid/getPlaidWebview';

import AddCounterPartyOption from './AddCounterPartyOption';
import { LinkStyled, SecurelyStyled } from '../AddCounterPartyModal.styles';

import { MAIN_ENTRY_PAGES } from '../constants/entryPages';
import { onOpenLinkBankAccountErrorModal } from '../utils/errorHandlers';

const AddBank = forwardRef(function AddBank(
  { handleClose, refetch, handleOpenManualInfoAddBankModal, actionsKit },
  ref
) {
  const { isWebView } = WebView;

  const { unitAccountId } = useUnitAccountId();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { redirectToLinkedAccounts } = useRedirectToLinkedAccounts(businessEntityUuid);

  const captureException = useCaptureException();
  const { openModal } = useModalContext();

  useModalDisplayTracker({
    elementName: 'add_bank_modal',
    elementText: 'add_bank_modal',
    appPageName: 'add_bank_modal'
  });

  const onCounterPartyCreatonError = (errorData) => {
    onOpenLinkBankAccountErrorModal(errorData, openModal);
  };

  const onCounterPartyReceived = (counterParty) => {
    if (typeof refetch === 'function') {
      refetch();
    }
    // Determine the entry page from the current URL.
    const entryPage = getLastSegmentUrl(document.location.pathname);

    // If the flow is triggered from the main entry pages (dashboard or dashboard-notfunded),
    // we'll open the modal and load data on the form.
    // Otherwise, we'll close the current modal and return to the entry page.
    if (entryPage && !MAIN_ENTRY_PAGES.includes(entryPage)) {
      handleClose();
      return;
    }

    if (counterParty) {
      openModal({
        name: MODAL_NAMES.DEPOSIT_MONEY_MODAL,
        counterParty,
        actionsKit
      });
      setTimeout(() => {
        handleClose();
      }, 1000);
      return;
    }

    handleClose();
  };

  const { isPlaidReady, plaid, isHandling, getPlaidLinkToken, isFetchingPlaidToken } = usePlaid({
    unitAccountId,
    businessEntityUuid,
    counterPartyFields: [
      'id',
      'bank',
      'name',
      'accountNumber',
      'accountType',
      'type',
      'status',
      'connectedType',
      'fullAccountNumber'
    ],
    onCounterPartyReceived,
    onCounterPartyCreatonError
  });

  useImperativeHandle(
    ref,
    () => ({
      exitPlaid() {
        if (isPlaidReady) {
          plaid.exit();
        }
      }
    }),
    [isPlaidReady, plaid]
  );

  const trackAddBankClick = useButtonPressedCallback({
    elementText: 'Add bank',
    elementName: 'Add_bank',
    appPageName: 'Add_bank_modal'
  });

  const clickAddBankHandler = () => {
    trackAddBankClick();

    if (redirectToLinkedAccounts({ useTopWindow: true, intent: LINK_INTENT })) {
      return;
    }

    // Handle on webview
    if (isWebView) {
      try {
        const { pathname } = document.location;

        linkPlaidWithNativeApp({
          pathName: pathname,
          unitAccountId,
          modalName: MODAL_NAMES.DEPOSIT_MONEY
        });
      } catch (error) {
        captureException(error, {
          level: 'warning'
        });
      }
    } else {
      getPlaidLinkToken();
    }
  };

  return (
    <>
      <AddCounterPartyOption
        description="Transfer money to & from another account"
        icon={
          <SharedAsset
            alt=""
            height={24}
            src="/s2/icon/temple-fill.svg"
            width={24}
          />
        }
        isDisable={isHandling || isFetchingPlaidToken}
        isLoading={isHandling || isFetchingPlaidToken}
        onClick={clickAddBankHandler}
        title="Add bank"
      />
      <Stack
        alignItems="center"
        justifyContent="center"
      >
        <SecurelyStyled
          direction="row"
          spacing="8px"
        >
          <SharedAsset
            alt=""
            height={20}
            src="/s2/icon/lock-outline.svg"
            width={20}
          />
          <Text size="sm">We use Plaid to securely link accounts</Text>
        </SecurelyStyled>
      </Stack>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="center"
        spacing="8px"
      >
        <Text size="sm">
          <LinkStyled
            color="info"
            onClick={handleOpenManualInfoAddBankModal}
          >
            Can’t find your bank in Plaid?{' '}
          </LinkStyled>
        </Text>
      </Stack>
      {!isWebView && (
        <Stack
          alignItems="center"
          justifyContent="center"
        >
          <Text size="md">
            <LinkStyled
              color="info"
              href={OWNER_CONTRIBUTIONS_URL}
              rel="noreferrer"
              target="_blank"
            >
              See the benefits
            </LinkStyled>{' '}
            of adding personal funds to a business account.
          </Text>
        </Stack>
      )}
    </>
  );
});

export default AddBank;

AddBank.propTypes = {
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  handleOpenManualInfoAddBankModal: PropTypes.func,
  actionsKit: PropTypes.shape({
    resetSearchParameters: PropTypes.func
  })
};
